import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError, downloadInvoice, fileUrl, STATUS_LABELS, STATUS_COLORS } from "@/lib/api";
import { toast } from "sonner";
import { FilePdf, PencilSimple, Eye, Trash, UploadSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["pending", "assigned", "en_route", "arrived", "completed", "cancelled"];

export function StatusBadge({ status }) {
  return (
    <Badge data-testid={`status-badge-${status}`} variant="outline"
      className={`rounded-sm border font-bold uppercase tracking-wider ${STATUS_COLORS[status] || ""}`}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-MY", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function DetailDialog({ sub, onClose, onRefresh, canEdit, canUpdateStatus, staff }) {
  const { user } = useAuth();
  const [full, setFull] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("");
  const [payBusy, setPayBusy] = useState(false);

  useEffect(() => {
    api.get(`/submissions/${sub.id}`).then((r) => setFull(r.data)).catch(() => {});
  }, [sub.id]);

  useEffect(() => {
    setForm({
      status: sub.status,
      runner_id: sub.runner_id || "",
      driver_id: sub.driver_id || "",
      submitted_at: sub.submitted_at ? sub.submitted_at.slice(0, 16) : "",
      notes: sub.notes || "",
      runner_comm_rm: sub.pricing?.runner_commission_type === "flat" && sub.pricing?.runner_commission_value != null ? String(sub.pricing.runner_commission_value) : "",
      runner_comm_pct: ["pct", "percent"].includes(sub.pricing?.runner_commission_type) && sub.pricing?.runner_commission_value != null ? String(sub.pricing.runner_commission_value) : "",
      driver_comm_rm: sub.pricing?.driver_commission_type === "flat" && sub.pricing?.driver_commission_value != null ? String(sub.pricing.driver_commission_value) : "",
      driver_comm_pct: ["pct", "percent"].includes(sub.pricing?.driver_commission_type) && sub.pricing?.driver_commission_value != null ? String(sub.pricing.driver_commission_value) : "",
      price_tolls: sub.pricing?.tolls != null ? String(sub.pricing.tolls) : "",
      price_parking_fee: sub.parking_fee_per_day != null ? String(sub.parking_fee_per_day) : "",
      price_parking_days: sub.parking_days != null ? String(sub.parking_days) : "",
      price_extra_tyres: sub.pricing?.extra_tyres != null ? String(sub.pricing.extra_tyres) : "",
      price_discount: sub.pricing?.discount != null ? String(sub.pricing.discount) : "",
    });
  }, [sub]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/submissions/${sub.id}/status`, { status });
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadPayment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!payMethod) { toast.error("Select a payment method first"); return; }
    setPayBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/submissions/${sub.id}/payment`, { method: payMethod, receipt: dataUrl });
      toast.success("Payment recorded");
      const r2 = await api.get(`/submissions/${sub.id}`);
      setFull(r2.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPayBusy(false);
    }
  };

  const d = full || sub;

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = { status: form.status, runner_id: form.runner_id, driver_id: form.driver_id, notes: form.notes };
      if (form.submitted_at) payload.submitted_at = new Date(form.submitted_at).toISOString();
      if (d.type === "outsource" && d.pricing && d.pricing.total) {
        const commissions = {};
        if (form.runner_comm_rm !== "") { commissions.runner_type = "flat"; commissions.runner_value = parseFloat(form.runner_comm_rm) || 0; }
        else if (form.runner_comm_pct !== "") { commissions.runner_type = "percent"; commissions.runner_value = parseFloat(form.runner_comm_pct) || 0; }
        if (form.driver_comm_rm !== "") { commissions.driver_type = "flat"; commissions.driver_value = parseFloat(form.driver_comm_rm) || 0; }
        else if (form.driver_comm_pct !== "") { commissions.driver_type = "percent"; commissions.driver_value = parseFloat(form.driver_comm_pct) || 0; }
        if (Object.keys(commissions).length) payload.commissions = commissions;
        if (form.price_tolls !== "") payload.tolls = parseFloat(form.price_tolls) || 0;
        if (form.price_parking_fee !== "") payload.parking_fee_per_day = parseFloat(form.price_parking_fee) || 0;
        if (form.price_parking_days !== "") payload.parking_days = parseInt(form.price_parking_days) || 1;
        if (form.price_extra_tyres !== "") payload.extra_tyres = parseFloat(form.price_extra_tyres) || 0;
        if (form.price_discount !== "") payload.discount = parseFloat(form.price_discount) || 0;
      }
      await api.patch(`/submissions/${sub.id}`, payload);
      toast.success("Submission updated");
      setEdit(false);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const imgEntries = d.images
    ? [["Front", d.images.front], ["Back", d.images.back], ["Left", d.images.left], ["Right", d.images.right],
       ["Roadtax", d.images.roadtax], ["NRIC Front", d.nric_front], ["NRIC Back", d.nric_back],
       ...(d.images.accident || []).map((a, i) => [`Accident ${i + 1}`, a]),
       ...((d.incident?.police_report) || []).map((p, i) => [`Police Report ${i + 1}`, p])].filter(([, v]) => v)
    : [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-border bg-background" data-testid="submission-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 font-display">
            <span className="font-mono text-primary">{d.job_no}</span>
            <StatusBadge status={d.status} />
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {d.type === "outsource" ? "Outsource Towing" : "Inhouse Referral"}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-px border border-border/60 bg-border/40 md:grid-cols-3">
          {[
            ["Submitted", fmtDT(d.submitted_at)],
            ["Submitted By", d.submitted_by?.name],
            ["Driver", d.driver_name || "—"],
            ["Runner", d.runner_name || "—"],
            ...(d.type === "inhouse"
              ? [["Company", d.company], ["Plate", d.vehicle_plate || "—"]]
              : [["Customer", d.customer?.name], ["NRIC", d.customer?.nric],
                 ["Insurance", d.customer?.insurance || "—"], ["Distance", `${d.distance_km} KM`],
                 ["From", d.from_address], ["To", d.to_address]]),
          ].map(([k, v]) => (
            <div key={k} className="bg-card p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{k}</p>
              <p className="mt-1 break-words text-sm font-semibold">{v || "—"}</p>
            </div>
          ))}
        </div>

        {d.pricing && d.pricing.total !== undefined && (
          <div className="rounded-sm border border-border">
            <div className="border-b border-border bg-card px-4 py-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Pricing Breakdown</div>
            <div className="grid grid-cols-2 gap-px bg-border/40 md:grid-cols-4">
              {[["Base", d.pricing.base_price], ["Extra KM", d.pricing.extra_charge], ["Tolls", d.pricing.tolls],
                ["Parking", d.pricing.parking_total], ["Extra Tyres", d.pricing.extra_tyres], ["Runner Comm.", d.pricing.runner_commission],
                ["Driver Comm.", d.pricing.driver_commission],
                ...(d.pricing.discount ? [["Discount", -d.pricing.discount]] : []),
                ["Total", d.pricing.total]].map(([k, v]) => (
                <div key={k} className="bg-card p-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{k}</p>
                  <p className={`font-mono text-sm font-bold ${k === "Total" ? "text-primary" : ""}`}>RM {Number(v ?? 0).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {d.discount?.amount > 0 && (
          <div className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-card px-4 py-3" data-testid="discount-applied-line">
            <Badge variant="outline" className="rounded-sm border-primary/50 bg-primary/10 font-bold uppercase tracking-wider text-primary">Discount Applied</Badge>
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              RM {d.discount.amount.toFixed(2)} · by {d.discount.by || "—"}
            </span>
          </div>
        )}

        {imgEntries.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Photo Evidence</p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {imgEntries.map(([label, src]) => (
                <a key={label} href={fileUrl(src)} target="_blank" rel="noreferrer" className="group relative aspect-square overflow-hidden rounded-sm border border-border">
                  <img src={fileUrl(src)} alt={label} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">{label}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {d.notes && <p className="rounded-sm border border-border bg-card p-4 text-sm text-muted-foreground">{d.notes}</p>}

        <div className="space-y-3 rounded-sm border border-border bg-card p-4" data-testid="payment-section">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Payment</p>
          {d.payment ? (
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="rounded-sm border-emerald-500/40 font-bold uppercase tracking-wider text-emerald-400" data-testid="payment-method-badge">
                {{ qr: "QR", credit_card: "Credit Card", bank_transfer: "Bank Transfer" }[d.payment.method] || d.payment.method}
              </Badge>
              <span className="text-xs text-muted-foreground">{d.payment.by} · {fmtDT(d.payment.at)}</span>
              {d.payment.receipt_path && (
                <a href={fileUrl(d.payment.receipt_path)} target="_blank" rel="noreferrer" data-testid="payment-receipt-link"
                  className="text-xs font-bold uppercase tracking-wider text-primary hover:text-amber-400 hover:underline">View Receipt</a>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="payment-empty">No payment recorded yet.</p>
          )}
          {(canEdit || user?.id === d.submitted_by?.id || user?.id === d.driver_id || user?.id === d.runner_id) && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Method</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger data-testid="payment-method-select" className="w-44 rounded-sm border-input bg-background"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="qr" data-testid="payment-method-qr">QR</SelectItem>
                    <SelectItem value="credit_card" data-testid="payment-method-cc">Credit Card</SelectItem>
                    <SelectItem value="bank_transfer" data-testid="payment-method-bank">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label>
                <input type="file" accept="image/*" className="hidden" data-testid="payment-receipt-input" onChange={uploadPayment} />
                <span className={`inline-flex cursor-pointer items-center gap-2 rounded-sm border border-primary/50 px-4 py-2 text-sm font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary/10 ${payBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="payment-upload-button">
                  <UploadSimple size={16} /> {payBusy ? "Uploading..." : "Upload Receipt"}
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          {canUpdateStatus && (
            <Select value={undefined} onValueChange={changeStatus}>
              <SelectTrigger data-testid="status-update-select" className="w-48 rounded-sm border-input bg-card focus:ring-primary">
                <SelectValue placeholder="Update status..." />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {d.has_invoice && (
            <Button data-testid="detail-download-invoice" variant="outline" onClick={() => downloadInvoice(d.id, d.invoice_no)}
              className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
              <FilePdf size={16} className="mr-2" /> {d.invoice_no}
            </Button>
          )}
          {canEdit && (
            <Button data-testid="edit-submission-button" variant="outline" onClick={() => setEdit(!edit)}
              className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
              <PencilSimple size={16} className="mr-2" /> {edit ? "Cancel Edit" : "Edit / Dispatch"}
            </Button>
          )}
          {["ceo", "coceo", "dev"].includes(user?.role) && d.status !== "draft" && (
            <Button variant="outline" data-testid="delete-submission-button"
              onClick={async () => {
                if (!window.confirm(`Delete ${d.job_no} permanently? This also removes its invoice.`)) return;
                try {
                  await api.delete(`/submissions/${d.id}`);
                  toast.success("Submission deleted");
                  onClose();
                  onRefresh();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-sm border-destructive/50 text-red-400 hover:bg-destructive/10 hover:text-red-300">
              <Trash size={16} className="mr-2" /> Delete
            </Button>
          )}
        </div>

        {edit && canEdit && (
          <div className="space-y-4 rounded-sm border border-primary/40 bg-primary/5 p-5" data-testid="edit-panel">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Admin Override</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-status-select" className="rounded-sm border-input bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date/Time Override</Label>
                <Input data-testid="edit-datetime-input" type="datetime-local" value={form.submitted_at}
                  onChange={(e) => setForm({ ...form, submitted_at: e.target.value })}
                  className="rounded-sm border-input bg-background font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Runner</Label>
                <Select value={form.runner_id} onValueChange={(v) => setForm({ ...form, runner_id: v })}>
                  <SelectTrigger data-testid="edit-runner-select" className="rounded-sm border-input bg-background"><SelectValue placeholder="Assign runner" /></SelectTrigger>
                  <SelectContent className="bg-popover">{(staff?.runners || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Driver</Label>
                <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
                  <SelectTrigger data-testid="edit-driver-select" className="rounded-sm border-input bg-background"><SelectValue placeholder="Assign driver" /></SelectTrigger>
                  <SelectContent className="bg-popover">{(staff?.drivers || []).map((dr) => <SelectItem key={dr.id} value={dr.id}>{dr.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {d.type === "outsource" && d.pricing && d.pricing.total !== undefined && (
              <div className="space-y-3 rounded-sm border border-border bg-background p-4" data-testid="price-edit-section">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Price Adjustment — recalculates total &amp; regenerates invoice</p>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  {[["Tolls (RM)", "price_tolls", "edit-price-tolls"], ["Parking RM/day", "price_parking_fee", "edit-price-parking-fee"],
                    ["Parking Days", "price_parking_days", "edit-price-parking-days"], ["Extra Tyres (RM)", "price_extra_tyres", "edit-price-extra-tyres"],
                    ["Discount (RM)", "price_discount", "edit-price-discount"]].map(([label, key, tid]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
                      <Input type="number" min="0" step="0.01" data-testid={tid} value={form[key] ?? ""}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {d.type === "outsource" && d.pricing && d.pricing.total !== undefined && (
              <div className="space-y-3 rounded-sm border border-border bg-background p-4" data-testid="commission-override-section">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Commission Override (this job only)</p>
                {[["runner", "Runner Commission"], ["driver", "Driver Commission"]].map(([rk, label]) => {
                  const rmVal = parseFloat(form[`${rk}_comm_rm`]) || 0;
                  const pctVal = parseFloat(form[`${rk}_comm_pct`]) || 0;
                  const preview = form[`${rk}_comm_rm`] !== "" ? rmVal : (pctVal / 100) * (d.pricing.total ?? 0);
                  return (
                    <div key={rk} className="grid grid-cols-[1fr_110px_110px_100px] items-end gap-3">
                      <Label className="pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</Label>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Fixed (RM)</Label>
                        <Input type="number" min="0" step="0.01" data-testid={`edit-${rk}-comm-rm`} value={form[`${rk}_comm_rm`]}
                          onChange={(e) => setForm({ ...form, [`${rk}_comm_rm`]: e.target.value, [`${rk}_comm_pct`]: e.target.value !== "" ? "" : form[`${rk}_comm_pct`] })}
                          placeholder="0.00" className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Percent (%)</Label>
                        <Input type="number" min="0" max="100" step="0.01" data-testid={`edit-${rk}-comm-pct`} value={form[`${rk}_comm_pct`]}
                          onChange={(e) => setForm({ ...form, [`${rk}_comm_pct`]: e.target.value, [`${rk}_comm_rm`]: e.target.value !== "" ? "" : form[`${rk}_comm_rm`] })}
                          placeholder="0" className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
                      </div>
                      <div className="pb-2 text-right font-mono text-sm font-bold text-primary" data-testid={`edit-${rk}-comm-preview`}>
                        RM {(preview || 0).toFixed(2)}
                      </div>
                    </div>
                  );
                })}
                <p className="text-[10px] text-muted-foreground">Fill one field per role — RM for a fixed amount, or % of this job's invoice total (after discount). Overrides feed payroll automatically.</p>
              </div>
            )}
            <Textarea data-testid="edit-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes" className="rounded-sm border-input bg-background" />
            <Button data-testid="edit-save-button" onClick={saveEdit} disabled={busy}
              className="rounded-sm bg-primary font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              {busy ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SubmissionsTable({ submissions, onRefresh, canEdit = false, canUpdateStatus = false, staff }) {
  const [selected, setSelected] = useState(null);

  if (!submissions.length) {
    return (
      <div className="rounded-sm border border-dashed border-input p-12 text-center" data-testid="submissions-empty">
        <Eye size={32} className="mx-auto text-muted-foreground" />
        <p className="mt-4 text-sm text-muted-foreground">No submissions yet.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-sm border border-border" data-testid="submissions-table">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Job No</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Type</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Driver / Runner</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Route / Company</TableHead>
            <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Total</TableHead>
            <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Submitted</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.map((s) => (
            <TableRow key={s.id} data-testid={`submission-row-${s.job_no}`} onClick={() => setSelected(s)}
              className="cursor-pointer border-border transition-colors hover:bg-muted/50">
              <TableCell className="font-mono text-sm font-bold text-primary">{s.job_no}</TableCell>
              <TableCell className="text-xs font-bold uppercase tracking-wider">{s.type}</TableCell>
              <TableCell><StatusBadge status={s.status} /></TableCell>
              <TableCell className="text-sm">{s.driver_name || "—"}{s.runner_name ? ` / ${s.runner_name}` : ""}</TableCell>
              <TableCell className="max-w-[220px] truncate text-sm text-muted-foreground">
                {s.type === "inhouse" ? s.company : `${s.from_address?.split(",")[0]} → ${s.to_address?.split(",")[0]}`}
              </TableCell>
              <TableCell className="text-right font-mono text-sm font-bold">
                {s.pricing?.total !== undefined ? `RM ${s.pricing.total.toFixed(2)}` : "—"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDT(s.submitted_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {selected && (
        <DetailDialog sub={selected} onClose={() => setSelected(null)} onRefresh={() => { setSelected(null); onRefresh(); }}
          canEdit={canEdit} canUpdateStatus={canUpdateStatus} staff={staff} />
      )}
    </div>
  );
}
